import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useAuth } from '../contexts/AuthContext';
import { purchaseCredits, confirmPurchase, getWallet, listPaymentMethods, attachPaymentMethod, getStripeCustomer, createStripeCustomer, initiateMobileMoneyPayment } from '../services/api';
import './PaymentModal.css';

const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';
const stripePromise = publishableKey ? loadStripe(publishableKey) : null;

const QUICK_PICKS = [1, 3, 5, 10];

const generateOrderId = () =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '15px',
      color: '#0F172A',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      fontWeight: '400',
      lineHeight: '24px',
      '::placeholder': { color: '#94A3B8' },
    },
    invalid: { color: '#EF4444' },
  },
};

const delay = (ms) => new Promise(r => setTimeout(r, ms));

function GrantsCheckoutForm({ onClose }) {
  const stripe = useStripe();
  const elements = useElements();
  const { markPaid, user, creditBalance, stripeCustomerId, updateStripeCustomerId } = useAuth();

  const [qty, setQty] = useState(1);
  const [cardComplete, setCardComplete] = useState(false);
  const [cardName, setCardName] = useState('');
  const [step, setStep] = useState('form'); // 'form' | 'processing' | 'success'
  const [error, setError] = useState('');
  const [savedMethods, setSavedMethods] = useState([]);
  const [selectedPmId, setSelectedPmId] = useState(''); // '' = new card
  const [loadingMethods, setLoadingMethods] = useState(true);
  const [payTab, setPayTab] = useState('card'); // 'card' | 'mobile'
  const [msisdn, setMsisdn] = useState('');
  const [processingType, setProcessingType] = useState('card');

  useEffect(() => {
    if (!user?.uid) { setLoadingMethods(false); return; }
    listPaymentMethods(user.uid)
      .then((res) => {
        const methods = Array.isArray(res) ? res : (res?.data ?? []);
        setSavedMethods(methods);
        if (methods.length > 0) setSelectedPmId(methods[0].id);
      })
      .catch(() => {})
      .finally(() => setLoadingMethods(false));
  }, [user?.uid]);

  const handleQtyChange = (val) => {
    const n = parseInt(val, 10);
    if (!isNaN(n) && n >= 1) setQty(n);
    else if (val === '') setQty('');
  };

  const handleQtyBlur = () => {
    if (!qty || qty < 1) setQty(1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!stripe || !elements) return;

    try {
      let pmId;
      setProcessingType('card');

      if (selectedPmId) {
        // Saved card path — skip card element entirely
        pmId = selectedPmId;
        setStep('processing');
      } else {
        // New card path
        const cardElement = elements.getElement(CardElement);
        if (!cardElement) { setError('Payment form not ready. Please refresh.'); return; }
        if (!cardComplete) { setError('Please complete your card details.'); return; }
        if (!cardName.trim()) { setError('Please enter the name on your card.'); return; }

        const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
          type: 'card',
          card: cardElement,
          billing_details: { name: cardName, email: user?.email || undefined },
        });
        if (pmError) { setError(pmError.message); return; }
        pmId = paymentMethod.id;
        setStep('processing');
      }

      // Ensure Stripe customer exists before creating PaymentIntent
      let activeCid = stripeCustomerId;
      if (activeCid) {
        try {
          await getStripeCustomer(activeCid);
        } catch (_) {
          // Customer not found — create a new one
          const res = await createStripeCustomer(user.uid, user.email, user.name);
          activeCid = res.id || res.stripeCustomerId || '';
          updateStripeCustomerId(activeCid);
        }
      } else {
        const res = await createStripeCustomer(user.uid, user.email, user.name);
        activeCid = res.id || res.stripeCustomerId || '';
        updateStripeCustomerId(activeCid);
      }

      // Backend creates PaymentIntent
      const purchaseRes = await purchaseCredits(user.uid, effectiveQty, pmId, activeCid);
      const clientSecret = purchaseRes.clientSecret || purchaseRes.client_secret;
      if (!clientSecret) { setStep('form'); setError('Failed to create payment intent. Please try again.'); return; }

      // Confirm payment on Stripe
      const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: pmId,
      });
      if (confirmError) { setStep('form'); setError(confirmError.message); return; }

      // Notify backend to finalize wallet credit
      if (paymentIntent?.status === 'succeeded') {
        await confirmPurchase(user.uid, paymentIntent.id);
      }

      // Save card for future use (new card only, fire-and-forget)
      if (!selectedPmId) {
        attachPaymentMethod(user.uid, pmId).catch(() => {});
      }

      // Poll wallet until balance increases
      const prevBalance = creditBalance;
      let newBalance = prevBalance;
      for (let i = 0; i < 10; i++) {
        await delay(1500);
        try {
          const walletRes = await getWallet(user.uid);
          const seaBal = walletRes.wallet?.sea ?? 0;
          if (seaBal > prevBalance) { newBalance = seaBal; break; }
        } catch (_) {}
      }

      setStep('success');
      setTimeout(() => markPaid(newBalance), 1400);
    } catch (err) {
      setStep('form');
      setError(err?.message || 'Payment failed. Please try again.');
    }
  };

  const handleMobileSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const phone = msisdn.trim();
    if (!phone) { setError('Please enter your mobile number.'); return; }
    if (!/^\+\d{10,15}$/.test(phone)) {
      setError('Enter a valid number starting with + and country code (e.g. +256700123456).');
      return;
    }

    setProcessingType('mobile');
    setStep('processing');

    const eff = Math.max(parseInt(qty, 10) || 1, 2);
    const orderId = generateOrderId();

    try {
      await initiateMobileMoneyPayment(phone, eff * 0.50, orderId);

      // Poll wallet until balance increases (mobile approval takes time)
      const prevBalance = creditBalance;
      let newBalance = prevBalance;
      for (let i = 0; i < 20; i++) {
        await delay(3000);
        try {
          const walletRes = await getWallet(user.uid);
          const seaBal = walletRes.wallet?.sea ?? 0;
          if (seaBal > prevBalance) { newBalance = seaBal; break; }
        } catch (_) {}
      }

      if (newBalance > prevBalance) {
        setStep('success');
        setTimeout(() => markPaid(newBalance), 1400);
      } else {
        setStep('form');
        setError('Payment not confirmed. Please approve the prompt on your phone and try again.');
      }
    } catch (err) {
      setStep('form');
      setError(err?.message || 'Mobile money payment failed. Please try again.');
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && step !== 'processing') onClose();
  };

  const grantQty = parseInt(qty, 10) || 1;
  const effectiveQty = Math.max(grantQty, 2); // minimum $1.00 = 2 grants at $0.50 each

  return (
    <div className="pay-overlay" onClick={handleOverlayClick}>
      <div className="pay-modal pay-modal--grants" role="dialog" aria-modal="true">

        {/* ── Success ─────────────────────────────────────────────────── */}
        {step === 'success' && (
          <div className="pay-success">
            <div className="pay-success-icon">
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                <path d="M6 16l7 7 13-13" stroke="#16A34A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h2>Payment successful!</h2>
            <p>You received <strong>{effectiveQty} tracking grant{effectiveQty > 1 ? 's' : ''}</strong>.</p>
          </div>
        )}

        {/* ── Processing ──────────────────────────────────────────────── */}
        {step === 'processing' && (
          <div className="pay-processing">
            <div className="pay-processing-spinner" />
            {processingType === 'mobile' ? (
              <>
                <h2>Waiting for approval…</h2>
                <p>Check your phone and approve the mobile money prompt</p>
              </>
            ) : (
              <>
                <h2>Processing payment…</h2>
                <p>Please don't close this window</p>
              </>
            )}
          </div>
        )}

        {/* ── Main form (two-column) ───────────────────────────────────── */}
        {step === 'form' && (
          <>
            {/* Left panel */}
            <div className="pay-left">
              <div className="pay-left-inner">
                <div className="pay-brand">
                  <img src="/Logo-01.png" alt="Ridelink" className="pay-brand-logo" />
                </div>

                <h2 className="pay-left-title">Buy Tracking Credits</h2>
                <p className="pay-left-desc">
                  Each credit gives you 1 shipment tracking. Buy as many as you need.
                </p>

                <ul className="pay-left-features">
                  {[
                    '1 credit  = 1 shipment tracked',
                    'Credits never expire',
                    'No subscription required',
                    'Secure checkout via Stripe',
                  ].map((f) => (
                    <li key={f}>
                      <span className="pay-feat-check">
                        <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                          <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </span>
                      {f}
                    </li>
                  ))}
                </ul>

                <div className="pay-left-pricing">
                  <span className="pay-left-amount">$0.50</span>
                  <span className="pay-left-per">per credit </span>
                </div>
              </div>
            </div>

            {/* Right panel */}
            <div className="pay-right">
              <button
                type="button"
                className="pay-modal-close"
                onClick={onClose}
                aria-label="Close"
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>

              <div className="pay-right-inner">
                <h3 className="pay-right-title">Payment details</h3>
                <p className="pay-right-subtitle">Choose your grant quantity and pay once</p>

                {/* Payment method tabs */}
                <div className="pay-tabs">
                  <button
                    type="button"
                    className={`pay-tab${payTab === 'card' ? ' active' : ''}`}
                    onClick={() => { setPayTab('card'); setError(''); }}
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                      <rect x="2" y="5" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="2"/>
                      <path d="M2 10h20" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                    Card
                  </button>
                  <button
                    type="button"
                    className={`pay-tab${payTab === 'mobile' ? ' active' : ''}`}
                    onClick={() => { setPayTab('mobile'); setError(''); }}
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                      <rect x="5" y="2" width="14" height="20" rx="2" stroke="currentColor" strokeWidth="2"/>
                      <circle cx="12" cy="17" r="1" fill="currentColor"/>
                    </svg>
                    Mobile Money
                  </button>
                </div>

                <form onSubmit={payTab === 'mobile' ? handleMobileSubmit : handleSubmit} className="pay-grants-form">
                  {/* Quantity picker */}
                  <div className="pay-field">
                    <label className="pay-label">Number of credits</label>
                    <div className="pay-qty-picker">
                      {QUICK_PICKS.map((n) => (
                        <button
                          key={n}
                          type="button"
                          className={`pay-qty-btn${qty === n ? ' active' : ''}`}
                          onClick={() => setQty(n)}
                        >
                          {n}
                        </button>
                      ))}
                      <input
                        type="number"
                        min="1"
                        value={qty}
                        onChange={(e) => handleQtyChange(e.target.value)}
                        onBlur={handleQtyBlur}
                        className="pay-qty-input"
                        placeholder="Custom"
                        aria-label="Custom quantity"
                      />
                    </div>
                    <p className="pay-qty-total">
                      {effectiveQty} credit{effectiveQty > 1 ? 's' : ''} = <strong>${(effectiveQty * 0.50).toFixed(2)}</strong>
                    </p>
                  </div>

                  {payTab === 'card' && (
                    <>
                      {/* Saved cards */}
                      {!loadingMethods && savedMethods.length > 0 && (
                        <div className="pay-field">
                          <label className="pay-label">Payment method</label>
                          <div className="pay-saved-methods">
                            {savedMethods.map((pm) => (
                              <button
                                key={pm.id}
                                type="button"
                                className={`pay-saved-btn${selectedPmId === pm.id ? ' active' : ''}`}
                                onClick={() => setSelectedPmId(pm.id)}
                              >
                                <span className="pay-card-brand">{pm.card.brand}</span>
                                •••• {pm.card.last4}
                                <span className="pay-card-exp">{pm.card.exp_month}/{String(pm.card.exp_year).slice(-2)}</span>
                              </button>
                            ))}
                            <button
                              type="button"
                              className={`pay-saved-btn${selectedPmId === '' ? ' active' : ''}`}
                              onClick={() => setSelectedPmId('')}
                            >
                              + Use a new card
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Card element — only shown for new card */}
                      {selectedPmId === '' && (
                        <>
                          <div className="pay-field">
                            <label className="pay-label">Card information</label>
                            <div className="pay-card-element-wrap">
                              <CardElement
                                options={CARD_ELEMENT_OPTIONS}
                                onChange={(e) => setCardComplete(e.complete)}
                              />
                            </div>
                          </div>

                          <div className="pay-field">
                            <label htmlFor="pay-card-name" className="pay-label">Cardholder name</label>
                            <input
                              id="pay-card-name"
                              type="text"
                              value={cardName}
                              onChange={(e) => setCardName(e.target.value)}
                              placeholder="Name as on card"
                              className="pay-text-input"
                              autoComplete="cc-name"
                            />
                          </div>
                        </>
                      )}
                    </>
                  )}

                  {payTab === 'mobile' && (
                    <div className="pay-field">
                      <label htmlFor="pay-msisdn" className="pay-label">Mobile money number</label>
                      <div className="pay-phone-wrap">
                        <svg className="pay-phone-icon" width="15" height="15" viewBox="0 0 24 24" fill="none">
                          <rect x="5" y="2" width="14" height="20" rx="2" stroke="currentColor" strokeWidth="2"/>
                          <circle cx="12" cy="17" r="1" fill="currentColor"/>
                        </svg>
                        <input
                          id="pay-msisdn"
                          type="tel"
                          value={msisdn}
                          onChange={(e) => setMsisdn(e.target.value)}
                          placeholder="+256700123456"
                          className="pay-text-input pay-phone-input"
                          autoComplete="tel"
                        />
                      </div>
                      <p className="pay-qty-total">Include country code (e.g. +256 for Uganda)</p>
                    </div>
                  )}

                  {error && (
                    <div className="pay-error">{error}</div>
                  )}

                  {payTab === 'card' ? (
                    <button
                      type="submit"
                      disabled={!stripe || (selectedPmId === '' && (!cardComplete || !cardName.trim()))}
                      className="pay-submit-btn"
                    >
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                        <rect x="3" y="11" width="18" height="11" rx="2" stroke="currentColor" strokeWidth="2"/>
                        <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                      Pay ${(effectiveQty * 0.50).toFixed(2)}
                    </button>
                  ) : (
                    <button
                      type="submit"
                      disabled={!msisdn.trim()}
                      className="pay-submit-btn pay-submit-btn--mobile"
                    >
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                        <rect x="5" y="2" width="14" height="20" rx="2" stroke="currentColor" strokeWidth="2"/>
                        <circle cx="12" cy="17" r="1" fill="currentColor"/>
                      </svg>
                      Pay ${(effectiveQty * 0.50).toFixed(2)} via Mobile Money
                    </button>
                  )}

                  <div className="pay-secure-note">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                      <rect x="3" y="11" width="18" height="11" rx="2" stroke="currentColor" strokeWidth="2"/>
                      <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                    {payTab === 'card' ? 'Secured by Stripe · No subscription' : 'Secured by Iotec · No subscription'}
                  </div>
                </form>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function PaymentModal() {
  const { isPaymentModalOpen, closePaymentModal } = useAuth();

  if (!isPaymentModalOpen) return null;

  if (!publishableKey) {
    return (
      <div className="pay-overlay" onClick={closePaymentModal}>
        <div className="pay-modal" style={{ maxWidth: 420, padding: 32 }}>
          <button className="pay-modal-close" onClick={closePaymentModal} aria-label="Close">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
          <p style={{ color: '#B45309', marginTop: 24, fontSize: 14, lineHeight: 1.6 }}>
            Missing <code>REACT_APP_STRIPE_PUBLISHABLE_KEY</code>. Add it to your <code>.env</code> file and restart the app.
          </p>
        </div>
      </div>
    );
  }

  return (
    <Elements stripe={stripePromise}>
      <GrantsCheckoutForm onClose={closePaymentModal} />
    </Elements>
  );
}
